import { Select, Skeleton } from 'antd';
import { AvatarComponent, PrimaryButton, BreadCrumb, dynamicNoSsrWithSkeleton } from '@lib/common';
import type { MenuProps } from 'antd';
import { useTranslation } from 'next-i18next';
import { LogoutOutlined, UserOutlined, DownOutlined, CrownOutlined } from '@ant-design/icons';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthentication } from '../../hooks';
import {
  currentLocaleSelector,
  getTestMakerLocaleStatusSelector,
  PageLayoutStatus,
  signOutStatusSelector,
} from '../../redux/layout';
import { useRouter } from 'next/router';
import { getUseCase } from '@lib/plugin-redux-core';
import {
  CHANGE_TEST_MAKER_LOCALE_USE_CASE,
  GET_TEST_MAKER_LOCALE_USE_CASE,
  SIGN_OUT_USE_CASE,
} from '@module/user';
import { PathBuilder, routeManager } from '@lib/route-manager';
import { buildManageSubscriptionUrl } from '../../utils/auth-guard.util';
import styled from 'styled-components';
import Image from 'next/image';
import { useSpaceFolderBreadcrumbExtraItems } from '../space/hooks';

const LogoutConfirmModalDynamic = dynamic(() => import('./log-out-confirm.modal'), dynamicNoSsrWithSkeleton);

const LANGUAGE_OPTIONS = [
  { label: 'English', value: 'en', flag: '/icons/us-flag.svg' },
  { label: 'Tiếng Việt', value: 'vi', flag: '/icons/vn-flag.svg' },
] as const;

const capitalizeFirstLetter = (value?: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

const StyledLanguageSelection = styled(Select)`
  border: none !important;
  min-width: 120px;
  transition: opacity 0.2s ease-in-out;

  .ant-select-selector {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
  }

  .ant-select-selection-item {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    color: #000000 !important;
    font-weight: 500 !important;
  }

  .ant-select-arrow {
    color: #000000 !important;
  }

  &:hover {
    opacity: 0.8;
    .ant-select-selector {
      border-color: rgba(255, 255, 255, 0.4) !important;
    }
  }

  .ant-select-dropdown {
    .ant-select-item {
      color: #000000 !important;

      &:hover {
        background-color: #f5f5f5 !important;
      }

      &.ant-select-item-option-selected {
        background-color: #e6f7ff !important;
        color: #000000 !important;
      }
    }
  }

  .flag-icon {
    border-radius: 2px;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LanguageOptionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #000000;
`;

const LanguageSkeletonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  opacity: 0.5;
`;

const LanguageSelectionSkeleton = memo(() => (
  <LanguageSkeletonWrapper>
    <Skeleton.Avatar
      active
      shape="square"
      size={16}
      style={{ width: 20, height: 14, borderRadius: 2, minWidth: 'unset' }}
    />
    <Skeleton.Button
      active
      size="small"
      style={{ width: 64, minWidth: 'unset', height: 16, borderRadius: 4 }}
    />
    <Skeleton.Avatar
      active
      shape="square"
      size={10}
      style={{ width: 10, height: 10, borderRadius: 2, minWidth: 'unset' }}
    />
  </LanguageSkeletonWrapper>
));

LanguageSelectionSkeleton.displayName = 'LanguageSelectionSkeleton';

const selectSuffixIcon = (
  <DownOutlined style={{ color: '#000000', fontSize: '12px', pointerEvents: 'none' }} />
);

const languageOptions = LANGUAGE_OPTIONS.map((option) => ({
  label: (
    <LanguageOptionLabel>
      <Image
        src={option.flag}
        alt={option.label}
        width={16}
        height={12}
        className="flag-icon"
      />
      <span style={{ color: '#000000', fontWeight: 400 }}>{option.label}</span>
    </LanguageOptionLabel>
  ),
  value: option.value,
}));

const HeaderContainer = memo(() => {
  const { t } = useTranslation('common');
  const { publicRuntimeConfig } = getConfig();
  const { profile, isTokenValid } = useAuthentication();
  const [isOpenLogoutConfirmModal, setModalAlertVisibility] = useState(false);
  const dispatch = useDispatch();
  const signOutStatus = useSelector(signOutStatusSelector);
  const router = useRouter();
  const currentLocale = useSelector(currentLocaleSelector);
  const getTestMakerLocaleStatus = useSelector(getTestMakerLocaleStatusSelector);

  const getTestMakerLocaleUseCase = useMemo(
    () => getUseCase(GET_TEST_MAKER_LOCALE_USE_CASE, dispatch),
    [dispatch],
  );
  const signOutForAppUseCase = useMemo(() => getUseCase(SIGN_OUT_USE_CASE, dispatch), [dispatch]);
  const changeTestMakerLocaleUseCase = useMemo(
    () => getUseCase(CHANGE_TEST_MAKER_LOCALE_USE_CASE, dispatch),
    [dispatch],
  );

  useEffect(() => {
    getTestMakerLocaleUseCase.execute(null);
  }, [getTestMakerLocaleUseCase]);

  useEffect(() => {
    if (currentLocale && router.locale !== currentLocale) {
      router.push(router.asPath, router.asPath, { locale: currentLocale });
    }
  }, [currentLocale, router, router.locale, router.asPath]);

  useEffect(() => {
    if (signOutStatus === PageLayoutStatus.SUCCESS) {
      router.push(
        new PathBuilder()
          .withBasePath(routeManager.standard.identity.fullPath.signOut.path)
          .withQueryParam('clientId', publicRuntimeConfig.clientId)
          .withQueryParam('redirectUrl', router.asPath)
          .build(),
      );
    }
  }, [signOutStatus, router, router.asPath, publicRuntimeConfig.clientId]);

  const fullName = useMemo(
    () =>
      `${capitalizeFirstLetter(profile?.firstName)} ${capitalizeFirstLetter(profile?.lastName)}`.trim(),
    [profile?.firstName, profile?.lastName],
  );

  const handleSignOutSubmit = useCallback(() => {
    signOutForAppUseCase.execute({
      clientId: publicRuntimeConfig.clientId,
    });
  }, [publicRuntimeConfig.clientId, signOutForAppUseCase]);

  const handleNavigateToUserProfilePage = useCallback(() => {
    const url = new PathBuilder()
      .withBasePath(routeManager.standard.identity.fullPath.accountSetting.updateProfile.path)
      .withQueryParam('clientId', publicRuntimeConfig.clientId)
      .build();

    window.open(url, '_blank');
  }, [publicRuntimeConfig.clientId]);

  const handleNavigateToManageSubscription = useCallback(() => {
    window.open(buildManageSubscriptionUrl(), '_blank', 'noopener,noreferrer');
  }, []);

  const handleOpenLogoutModal = useCallback(() => {
    setModalAlertVisibility(true);
  }, []);

  const handleCloseLogoutModal = useCallback(() => {
    setModalAlertVisibility(false);
  }, []);

  const items: MenuProps['items'] = useMemo(
    () => [
      {
        key: '1',
        label: t('userProfile'),
        icon: <UserOutlined />,
        onClick: handleNavigateToUserProfilePage,
      },
      {
        key: '2',
        label: t('manageSubscription'),
        icon: <CrownOutlined />,
        onClick: handleNavigateToManageSubscription,
      },
      {
        key: '3',
        label: t('signOut'),
        icon: <LogoutOutlined />,
        onClick: handleOpenLogoutModal,
      },
    ],
    [t, handleNavigateToUserProfilePage, handleNavigateToManageSubscription, handleOpenLogoutModal],
  );

  const handleLogin = useCallback(() => {
    router.push(routeManager.standard.identity.fullPath.authentication.path);
  }, [router]);

  const handleLocaleChange = useCallback(
    (locale: string) => {
      changeTestMakerLocaleUseCase.execute(locale);
      router.push(router.asPath, router.asPath, { locale });
    },
    [changeTestMakerLocaleUseCase, router],
  );

  const languageSelection = useMemo(() => {
    if (getTestMakerLocaleStatus === PageLayoutStatus.EXECUTE) {
      return <LanguageSelectionSkeleton />;
    }

    return (
      <StyledLanguageSelection
        bordered={false}
        options={languageOptions}
        value={currentLocale || 'en'}
        onChange={handleLocaleChange}
        suffixIcon={selectSuffixIcon}
      />
    );
  }, [currentLocale, getTestMakerLocaleStatus, handleLocaleChange]);

  const spaceFolderExtraItems = useSpaceFolderBreadcrumbExtraItems();

  return (
    <>
      <HeaderWrapper>
        <BreadCrumb
          root="bricks"
          rootPageName="Your work"
          url={router.asPath}
          baseUrl={publicRuntimeConfig.env.APP_BASE_URL}
          menuOverrides={{ examination: 'examination', practice: 'practice' }}
          level={1}
          extraItems={spaceFolderExtraItems}
          t={t}
        />
        <HeaderActions>
          {languageSelection}

          {isTokenValid ? (
            <AvatarComponent userName={fullName} email={profile?.email} settingMenu={items} />
          ) : (
            <PrimaryButton onClick={handleLogin}>{t('login')}</PrimaryButton>
          )}
        </HeaderActions>
      </HeaderWrapper>

      <LogoutConfirmModalDynamic
        open={isOpenLogoutConfirmModal}
        onClose={handleCloseLogoutModal}
        onConfirm={handleSignOutSubmit}
        isSigningOut={signOutStatus === PageLayoutStatus.EXECUTE}
      />
    </>
  );
});

HeaderContainer.displayName = 'HeaderContainer';

export default HeaderContainer;
